import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ICON_BTN, BTN_PRIMARY, BTN_GHOST, INPUT } from "@/lib/styles";
import { Share2, X, Copy, Check, Link2Off, Loader2, MessageCircle, Mail, Send } from "lucide-react";
import { toast } from "sonner";

export function ShareButton({ targetType, targetId, iconOnly = true, label = "Compartir" }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicUrl = token ? `${window.location.origin}/p/${token}` : "";

  const openDialog = async (e) => {
    e?.stopPropagation();
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get("/share", { params: { target_type: targetType, target_id: targetId } });
      setToken(data.token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const createLink = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/share", { target_type: targetType, target_id: targetId });
      setToken(data.token);
      toast.success("Enlace público creado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    setLoading(true);
    try {
      await api.delete(`/share/${token}`);
      setToken(null);
      toast.success("Enlace desactivado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Notacora", text: "Te comparto esto desde Notacora:", url: publicUrl });
      } catch { /* cancelado */ }
    } else {
      copy();
    }
  };

  const MSG = "Te comparto esto desde Notacora \u2615";
  const enc = encodeURIComponent;
  const SHARE_TARGETS = [
    { id: "whatsapp", label: "WhatsApp", color: "#25D366", Icon: MessageCircle, url: () => `https://wa.me/?text=${enc(MSG + " " + publicUrl)}` },
    { id: "telegram", label: "Telegram", color: "#0088cc", Icon: Send, url: () => `https://t.me/share/url?url=${enc(publicUrl)}&text=${enc(MSG)}` },
    { id: "gmail", label: "Gmail", color: "#EA4335", Icon: Mail, url: () => `https://mail.google.com/mail/?view=cm&fs=1&su=${enc("Notacora")}&body=${enc(MSG + " " + publicUrl)}` },
    { id: "email", label: "Correo", color: "#64748b", Icon: Mail, url: () => `mailto:?subject=${enc("Notacora")}&body=${enc(MSG + " " + publicUrl)}` },
    { id: "x", label: "X", color: "#000000", text: "\uD835\uDD4F", url: () => `https://twitter.com/intent/tweet?text=${enc(MSG)}&url=${enc(publicUrl)}` },
    { id: "facebook", label: "Facebook", color: "#1877F2", text: "f", url: () => `https://www.facebook.com/sharer/sharer.php?u=${enc(publicUrl)}` },
  ];
  const shareVia = (t) => {
    if (t.id === "email") { window.location.href = t.url(); return; }
    window.open(t.url(), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <button onClick={openDialog} className={`${ICON_BTN} ${iconOnly ? "" : "text-xs"}`} title="Compartir" data-testid={`share-button-${targetType}`}>
        <Share2 size={18} />
        {!iconOnly && <span className="hidden md:inline">{label}</span>}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="share-dialog">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-medium text-lg">Compartir públicamente</h3>
              <button onClick={() => setOpen(false)} className={ICON_BTN} data-testid="share-dialog-close"><X size={16} /></button>
            </div>

            {loading ? (
              <div className="py-10 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" size={22} /></div>
            ) : token ? (
              <div className="flex flex-col gap-4">
                <p className="font-body text-sm text-slate-500 dark:text-slate-400">
                  Cualquier persona con este enlace podrá ver esto en <b>modo lectura</b>, aunque no tenga cuenta.
                </p>
                <div className="flex gap-2">
                  <input readOnly value={publicUrl} className={`${INPUT} font-mono2 text-xs`} data-testid="share-link-input" onFocus={(e) => e.target.select()} />
                  <button onClick={copy} className={ICON_BTN} data-testid="share-copy-button" title="Copiar">
                    {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                  </button>
                </div>

                <div>
                  <p className="font-body text-xs text-slate-400 mb-2.5">Enviar por:</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="share-targets">
                    {SHARE_TARGETS.map((t) => (
                      <button key={t.id} onClick={() => shareVia(t)} className="flex flex-col items-center gap-1.5 group" data-testid={`share-via-${t.id}`}>
                        <span className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-110" style={{ backgroundColor: t.color }}>
                          {t.Icon ? <t.Icon size={20} /> : <span className="font-bold text-lg leading-none">{t.text}</span>}
                        </span>
                        <span className="font-body text-[10px] text-slate-500 dark:text-slate-400">{t.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-1">
                  <button onClick={revoke} className="inline-flex items-center gap-1.5 text-xs text-rose-500 hover:text-rose-400 font-medium" data-testid="share-revoke-button">
                    <Link2Off size={14} /> Dejar de compartir
                  </button>
                  <button onClick={shareNative} className={BTN_GHOST} data-testid="share-native-button">
                    <Share2 size={15} /> Más opciones
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <p className="font-body text-sm text-slate-500 dark:text-slate-400">
                  Genera un enlace público de solo lectura para compartir con quien quieras. Podrás desactivarlo cuando desees.
                </p>
                <button onClick={createLink} className={BTN_PRIMARY} data-testid="share-create-button">
                  <Share2 size={15} /> Crear enlace público
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
